import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X, Mail } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const LINKS = [
  { label: "Avant / Après", href: "#avant-apres", testid: "nav-before-after" },
  { label: "Services", href: "#services", testid: "nav-services" },
  { label: "Réalisations", href: "#realisations", testid: "nav-gallery" },
  { label: "Processus", href: "#processus", testid: "nav-process" },
  { label: "Contact", href: "#contact", testid: "nav-contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <button
          data-testid="header-logo"
          onClick={() => go("#hero")}
          className="flex items-center gap-3 group"
        >
          <span className="h-8 w-8 rotate-45 border border-gold flex items-center justify-center transition-transform duration-500 group-hover:rotate-[135deg]">
            <span className="h-2 w-2 bg-gold" />
          </span>
          <span className="font-display font-bold uppercase tracking-widest text-sm sm:text-base">
            Premium <span className="text-gold">Autoshine</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Navigation principale">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={l.testid}
              onClick={() => go(l.href)}
              className="text-sm text-white/70 hover:text-gold transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="header-phone-link"
            href="tel:5142690261"
            className="hidden sm:flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-5 py-2.5 text-sm font-medium text-gold backdrop-blur-md transition-[background-color,border-color,transform] duration-300 hover:bg-gold hover:text-black hover:-translate-y-0.5"
          >
            <Phone size={15} strokeWidth={2.2} />
            514-269-0261
          </a>
          <a
            data-testid="header-email-link"
            href="mailto:premium.autoshine.mtl@gmail.com"
            aria-label="premium.autoshine.mtl@gmail.com"
            className="hidden sm:flex items-center justify-center h-10 w-10 rounded-full border border-white/15 bg-white/5 text-white/70 backdrop-blur-md transition-[background-color,border-color,color,transform] duration-300 hover:border-gold/60 hover:text-gold hover:-translate-y-0.5"
          >
            <Mail size={15} strokeWidth={2.2} />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white/80 p-2"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.nav
          data-testid="mobile-menu"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-black/85 backdrop-blur-2xl border-t border-white/10 px-6 py-6 flex flex-col gap-4"
        >
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`mobile-${l.testid}`}
              onClick={() => go(l.href)}
              className="text-left text-base text-white/80 hover:text-gold transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <a data-testid="mobile-phone-link" href="tel:5142690261" className="flex items-center gap-2 text-gold font-medium pt-2">
            <Phone size={16} /> 514-269-0261
          </a>
          <a data-testid="mobile-email-link" href="mailto:premium.autoshine.mtl@gmail.com" className="flex items-center gap-2 text-white/80 hover:text-gold transition-colors duration-300">
            <Mail size={16} className="text-gold" /> premium.autoshine.mtl@gmail.com
          </a>
        </motion.nav>
      )}
    </motion.header>
  );
};
